/**
 * MobileOpportunitiesTab
 *
 * Card-based mobile list replacing the PrimeReact DataTable from OpportunitiesTab.
 * Glassmorphic cards with status-colored left border, search, filter chips, pagination.
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { Localize } from "../../../app/lang/langs";
import { getOpportunities } from "../../../components/clients/clientsSlice";
import { checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import MobileOpportunityModal from "./MobileOpportunityModal";
import moment from "moment";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const PAGE_SIZE = 15;

const STATUS_OPTIONS = [
  "Non démarré",
  "En cours",
  "Clos Gagné",
  "Clos Abandonné",
  "Clos Perdu",
  "Non pris en charge",
];

const CATEGORY_OPTIONS = [
  "Demande de Profil",
  "Besoin Profil Interne",
  "Projet Bureautique",
  "Projet Infra",
  "Infogérance",
  "Autres",
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const getStatusColor = (status) => {
  switch (status) {
    case "Non démarré":
      return "#9e9e9e";
    case "En cours":
      return "#1976d2";
    case "Clos Gagné":
      return "#2e7d32";
    case "Clos Abandonné":
      return "#ed6c02";
    case "Clos Perdu":
      return "#d32f2f";
    case "Non pris en charge":
      return "#c62828";
    default:
      return "#666";
  }
};

const getCategoryChipProps = (category) => {
  switch (category) {
    case "Demande de Profil":
      return { bgcolor: "rgba(25,118,210,0.1)", color: "#1976d2", border: "1px solid rgba(25,118,210,0.25)" };
    case "Besoin Profil Interne":
      return { bgcolor: "rgba(156,39,176,0.1)", color: "#9c27b0", border: "1px solid rgba(156,39,176,0.25)" };
    case "Projet Bureautique":
      return { bgcolor: "rgba(46,125,50,0.1)", color: "#2e7d32", border: "1px solid rgba(46,125,50,0.25)" };
    case "Projet Infra":
      return { bgcolor: "rgba(237,108,2,0.1)", color: "#ed6c02", border: "1px solid rgba(237,108,2,0.25)" };
    case "Infogérance":
      return { bgcolor: "rgba(0,0,0,0.06)", color: "#666", border: "1px solid rgba(0,0,0,0.12)" };
    case "Autres":
      return { bgcolor: "rgba(211,47,47,0.1)", color: "#d32f2f", border: "1px solid rgba(211,47,47,0.25)" };
    default:
      return { bgcolor: "rgba(0,0,0,0.06)", color: "#666", border: "1px solid rgba(0,0,0,0.12)" };
  }
};

const getPriorityChipProps = (priority) => {
  switch (priority) {
    case "Normale":
      return { bgcolor: "rgba(46,125,50,0.1)", color: "#2e7d32", border: "1px solid rgba(46,125,50,0.25)" };
    case "Urgent":
      return { bgcolor: "rgba(211,47,47,0.1)", color: "#d32f2f", border: "1px solid rgba(211,47,47,0.25)" };
    case "Faible":
      return { bgcolor: "rgba(0,0,0,0.06)", color: "#999", border: "1px solid rgba(0,0,0,0.12)" };
    default:
      return { bgcolor: "rgba(0,0,0,0.06)", color: "#666", border: "1px solid rgba(0,0,0,0.12)" };
  }
};

const formatDate = (date) => {
  if (!date) return "-";
  return moment(date).format("DD/MM/YYYY");
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileOpportunitiesTab({ addModalVisible, setAddModalVisible }) {
  const dispatch = useDispatch();
  const mounted = useRef(null);

  const oppList = useSelector((state) => state.clients.opportunities);

  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedOpp, setSelectedOpp] = useState(null);

  const canEdit = checkPermission(
    config.scopes.opportunities,
    config.permissionTypes.edit
  );

  // Fetch data on mount
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      dispatch(getOpportunities());
    }
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchText, selectedStatus, selectedCategory]);

  // Filter and search
  const filteredList = useMemo(() => {
    let data = oppList || [];

    if (selectedStatus) {
      data = data.filter((o) => o.status === selectedStatus);
    }
    if (selectedCategory) {
      data = data.filter((o) => o.category === selectedCategory);
    }

    if (searchText.trim()) {
      const terms = searchText.toLowerCase().split(" ").filter(Boolean);
      data = data.filter((o) => {
        const searchStr = [
          o.poste_name,
          o.title,
          o.localisation,
          o.team_name,
          o.status,
          o.category,
          o.priority,
          o.competences,
          o.description,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return terms.every((term) => searchStr.includes(term));
      });
    }

    return data;
  }, [oppList, searchText, selectedStatus, selectedCategory]);

  const totalPages = Math.ceil(filteredList.length / PAGE_SIZE);
  const paginatedList = filteredList.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  /* ---- Handlers ---- */
  const handleCardTap = (opp) => {
    if (canEdit) {
      setSelectedOpp(opp);
    }
  };

  const toggleFilter = (current, value, setter) => {
    setter(current === value ? null : value);
  };

  /* ---- Render ---- */
  return (
    <>
      {/* ---- Filters header ---- */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0,
          flexShrink: 0,
          backgroundColor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(24px) saturate(1.5)",
          WebkitBackdropFilter: "blur(24px) saturate(1.5)",
        }}
      >
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder={Localize("Search") + "..."}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{ color: "rgba(0,0,0,0.35)" }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& input": { fontSize: "16px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              backgroundColor: "rgba(255,255,255,0.5)",
              backdropFilter: "blur(12px)",
              transition: TRANSITION_SMOOTH,
              "& fieldset": {
                borderColor: "rgba(0,0,0,0.06)",
                transition: TRANSITION_SMOOTH,
              },
              "&:hover fieldset": {
                borderColor: "rgba(0,0,0,0.15)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgba(255,144,21,0.5)",
                boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
              },
            },
          }}
        />

        {/* Filter chips: Status */}
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mb: 1,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Chip
            label={Localize("All")}
            size="small"
            onClick={() => {
              setSelectedStatus(null);
              setSelectedCategory(null);
            }}
            variant={
              !selectedStatus && !selectedCategory ? "filled" : "outlined"
            }
            sx={{
              fontWeight: 600,
              fontSize: "0.72rem",
              height: 30,
              borderRadius: "999px",
              transition: TRANSITION_SMOOTH,
              flexShrink: 0,
              ...(!selectedStatus &&
                !selectedCategory && {
                  background:
                    "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(255,144,21,0.35)",
                  },
                }),
            }}
          />
          {STATUS_OPTIONS.map((status) => (
            <Chip
              key={status}
              label={status}
              size="small"
              onClick={() =>
                toggleFilter(selectedStatus, status, setSelectedStatus)
              }
              variant={selectedStatus === status ? "filled" : "outlined"}
              sx={{
                fontWeight: 600,
                fontSize: "0.72rem",
                height: 30,
                borderRadius: "999px",
                transition: TRANSITION_SMOOTH,
                flexShrink: 0,
                ...(selectedStatus === status && {
                  bgcolor: getStatusColor(status),
                  color: "#fff",
                  borderColor: getStatusColor(status),
                  boxShadow: `0 2px 8px ${getStatusColor(status)}40`,
                }),
                ...(selectedStatus !== status && {
                  borderColor: getStatusColor(status),
                  color: getStatusColor(status),
                  "&:hover": {
                    backgroundColor: `${getStatusColor(status)}10`,
                  },
                }),
              }}
            />
          ))}
        </Box>

        {/* Filter chips: Category */}
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            mb: 1,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {CATEGORY_OPTIONS.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              size="small"
              onClick={() =>
                toggleFilter(selectedCategory, cat, setSelectedCategory)
              }
              variant={selectedCategory === cat ? "filled" : "outlined"}
              sx={{
                fontWeight: 500,
                fontSize: "0.7rem",
                height: 28,
                borderRadius: "999px",
                transition: TRANSITION_SMOOTH,
                flexShrink: 0,
                ...(selectedCategory === cat && {
                  background:
                    "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                }),
                ...(selectedCategory !== cat && {
                  "&:hover": {
                    backgroundColor: "rgba(255,144,21,0.06)",
                  },
                }),
              }}
            />
          ))}
        </Box>

        {/* Results count */}
        <Typography
          variant="caption"
          sx={{
            mb: 1,
            px: 0.5,
            fontWeight: 700,
            letterSpacing: "0.04em",
            color: "rgba(0,0,0,0.45)",
            fontSize: "0.72rem",
            textTransform: "uppercase",
            display: "block",
          }}
        >
          {filteredList.length} {Localize("results")}
        </Typography>
      </Box>

      {/* ---- Card list ---- */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          px: 2,
          pt: 1.5,
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,144,21,0.18)",
            borderRadius: 3,
          },
        }}
      >
        {paginatedList.map((opp, index) => (
          <Fade in key={opp.id || index} timeout={300 + index * 50}>
            <Card
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: "18px",
                overflow: "hidden",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 100%)",
                backdropFilter: "blur(20px) saturate(1.6)",
                WebkitBackdropFilter: "blur(20px) saturate(1.6)",
                border: "1px solid rgba(255,255,255,0.6)",
                borderLeft: `4px solid ${getStatusColor(opp.status)}`,
                boxShadow: `0 8px 32px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.03), inset 0 1px 0 rgba(255,255,255,0.9), 0 0 0 0.5px ${getStatusColor(opp.status)}15`,
                transition: TRANSITION_SMOOTH,
                "&:hover": {
                  boxShadow: `0 12px 40px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04), 0 0 20px ${getStatusColor(opp.status)}12`,
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "scale(0.97)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                },
              }}
            >
              <CardActionArea
                onClick={() => handleCardTap(opp)}
                sx={{ p: 0, minHeight: 44 }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Row 1: Icon + ID/Poste + Status */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      mb: 1,
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {/* Avatar icon */}
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: "14px",
                          background:
                            "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow:
                            "0 4px 14px rgba(255,144,21,0.3), 0 1px 4px rgba(255,144,21,0.15), inset 0 1px 0 rgba(255,255,255,0.25)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      >
                        <WorkOutlineIcon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: "1rem",
                            lineHeight: 1.3,
                            letterSpacing: "-0.02em",
                            color: "rgba(0,0,0,0.9)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {opp.id ? `#${opp.id} ` : ""}
                          {opp.poste_name || "-"}
                        </Typography>
                        {opp.title && (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              color: "rgba(0,0,0,0.55)",
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {opp.title}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status chip */}
                    {opp.status && (
                      <Chip
                        label={opp.status}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.67rem",
                          height: 28,
                          borderRadius: "999px",
                          flexShrink: 0,
                          maxWidth: 150,
                          bgcolor: getStatusColor(opp.status),
                          color: "#fff",
                          boxShadow: `0 3px 10px ${getStatusColor(opp.status)}45, 0 1px 3px ${getStatusColor(opp.status)}25`,
                          transition: TRANSITION_SMOOTH,
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    )}
                  </Box>

                  {/* Row 2: Category + Priority + Sub-status chips */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      mb: 1,
                      pl: 7,
                      flexWrap: "wrap",
                    }}
                  >
                    {opp.priority && (
                      <Chip
                        label={opp.priority}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 26,
                          borderRadius: "999px",
                          transition: TRANSITION_SMOOTH,
                          ...getPriorityChipProps(opp.priority),
                        }}
                      />
                    )}
                    {opp.category && (
                      <Chip
                        label={opp.category}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          height: 26,
                          borderRadius: "999px",
                          transition: TRANSITION_SMOOTH,
                          ...getCategoryChipProps(opp.category),
                        }}
                      />
                    )}
                    {opp.sub_status && opp.sub_status !== "-" && (
                      <Chip
                        label={opp.sub_status}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          height: 24,
                          borderRadius: "999px",
                          background: "linear-gradient(135deg, rgba(255,144,21,0.06) 0%, rgba(255,193,0,0.04) 100%)",
                          borderColor: "rgba(255,144,21,0.2)",
                          color: "rgba(0,0,0,0.6)",
                          transition: TRANSITION_SMOOTH,
                        }}
                      />
                    )}
                  </Box>

                  {/* Row 3: Details */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      pl: 7,
                    }}
                  >
                    {/* Localisation */}
                    {opp.localisation && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <LocationOnOutlinedIcon
                          sx={{ fontSize: "0.9rem", color: "#FF9015" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.8rem",
                            color: "rgba(0,0,0,0.6)",
                            fontWeight: 500,
                          }}
                        >
                          {opp.localisation}
                        </Typography>
                      </Box>
                    )}

                    {/* Team */}
                    {opp.team_name && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <GroupsOutlinedIcon
                          sx={{ fontSize: "0.9rem", color: "#FF9015" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.8rem",
                            color: "rgba(0,0,0,0.6)",
                            fontWeight: 500,
                          }}
                        >
                          {opp.team_name}
                        </Typography>
                      </Box>
                    )}

                    {/* Start date */}
                    {opp.start_date && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <EventOutlinedIcon
                          sx={{ fontSize: "0.9rem", color: "#FF9015" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.8rem",
                            color: "rgba(0,0,0,0.6)",
                            fontWeight: 500,
                          }}
                        >
                          {formatDate(opp.start_date)}
                        </Typography>
                      </Box>
                    )}

                    {/* Profiles info */}
                    {(opp.profiles_number || opp.provided_profiles_number) && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <PeopleOutlineIcon
                          sx={{ fontSize: "0.9rem", color: "#FF9015" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.8rem",
                            color: "rgba(0,0,0,0.6)",
                            fontWeight: 500,
                          }}
                        >
                          {Localize("Profils recherchés")}:{" "}
                          {opp.profiles_number || 0} |{" "}
                          {Localize("Profils fournis")}:{" "}
                          {opp.provided_profiles_number || 0}
                        </Typography>
                      </Box>
                    )}

                    {/* Demanded by */}
                    {(opp.demanded_by_nom || opp.demanded_by_prenom) && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.72rem",
                          color: "rgba(0,0,0,0.4)",
                          fontWeight: 500,
                          pl: 0.25,
                        }}
                      >
                        Demandé par: {opp.demanded_by_prenom}{" "}
                        {opp.demanded_by_nom}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Fade>
        ))}

        {/* Empty state */}
        {paginatedList.length === 0 && (
          <Fade in timeout={400}>
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "24px",
                  background:
                    "linear-gradient(135deg, rgba(255,144,21,0.15) 0%, rgba(255,193,0,0.1) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,144,21,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  boxShadow:
                    "0 8px 32px rgba(255,144,21,0.12), 0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <InboxOutlinedIcon
                  sx={{ fontSize: 36, color: "#FF9015" }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "rgba(0,0,0,0.4)", fontWeight: 500 }}
              >
                Aucune opportunité trouvée.
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* ---- Pagination ---- */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            pt: 1.5,
            pb: 2,
            backgroundColor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(16px) saturate(1.4)",
            WebkitBackdropFilter: "blur(16px) saturate(1.4)",
            borderTop: "1px solid rgba(0,0,0,0.04)",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                fontWeight: 600,
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                  background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                  color: "#fff",
                },
              },
            }}
          />
        </Box>
      )}

      {/* ---- Opportunity Modal ---- */}
      <MobileOpportunityModal
        type={selectedOpp ? "EDIT" : "ADD"}
        visible={!!selectedOpp || addModalVisible}
        setVisible={selectedOpp ? () => setSelectedOpp(null) : setAddModalVisible}
        onViewItem={selectedOpp}
        setOnViewItem={setSelectedOpp}
      />
    </>
  );
}
